import type { FunctionArgs, FunctionReturnType } from "convex/server";
import type { api } from "../../convex/_generated/api";

/**
 * An event as the client sees it. Derived from the query's return type rather
 * than written out, so it cannot drift from what the server sends — same reason
 * as `members.ts`.
 */
export type Event = FunctionReturnType<typeof api.events.list>[number];

/**
 * Which of the two shapes this row is in.
 *
 * `startDate` is the discriminator rather than the absence of `startAt`,
 * because "absent" is also what a half-written row would look like and this
 * should read as a positive fact. The backend refuses to store both — see
 * `setWhen` in `convex/events.ts` — so exactly one is true.
 */
export const isAllDay = (event: Event) => event.startDate !== undefined;

/**
 * An event as it appears on **one day**.
 *
 * A non-recurring event has exactly one of these and it is the row itself. A
 * series has one per expanded occurrence, each carrying the series' `_id` — so
 * tapping any of them opens the series, which is the whole of what "a series is
 * all-or-nothing" means this slice.
 *
 * `key` exists because `_id` no longer identifies a row on screen: twelve
 * Tuesdays share one. It is a React key and nothing else — it is not stored,
 * not routed on, and not an occurrence's identity. Giving an occurrence a real
 * identity is the precondition for editing one, and that is a separate slice.
 *
 * The type lives here rather than in `recurrence.ts` so that `groupByDay` can
 * name it without the two modules importing each other.
 */
export type Occurrence = Event & { key: string };

/**
 * The four presets, taken from the mutation's own argument rather than written
 * out again. Adding a fifth on the backend is then a type error here until the
 * picker offers it, which is the direction the dependency should run — the
 * server is what can actually build the rule.
 */
export type Repeat = NonNullable<FunctionArgs<typeof api.events.setRepeat>["repeat"]>;

/**
 * The day of the month a row starts on, read in its own zone. Null for a
 * half-written row.
 *
 * A number, not a formatted string: the one caller asks whether it is past the
 * 28th, and `format.ts` returns locale digits, which `Number()` cannot always
 * read back. Display goes through `format.ts`; arithmetic comes from here.
 */
export function startDayOfMonth(event: Event): number | null {
  const anchor = startAnchor(event);
  return anchor === null ? null : Number(dateIn(anchor.at.getTime(), anchor.tz).slice(8));
}

/**
 * The moment to read a row's own calendar fields from, and **the zone to read
 * them in** — which is not the household's and not the device's.
 *
 * A timed row is read in the zone it was authored in, because that is what its
 * weekday and its day-of-month are facts about. An all-day row is read in UTC,
 * which is not a zone choice at all: its date has been carried as UTC midnight
 * since it was parsed, and reading it anywhere else is what would move a 3 June
 * birthday to the 2nd for half the planet.
 *
 * Returns null for a half-written row that is in neither shape.
 */
export function startAnchor(event: Event): { at: Date; tz: string } | null {
  if (event.startDate !== undefined) {
    const at = Date.parse(`${event.startDate}T00:00:00Z`);
    return Number.isFinite(at) ? { at: new Date(at), tz: "UTC" } : null;
  }
  if (event.startAt !== undefined && event.timezone !== undefined) {
    return { at: new Date(event.startAt), tz: event.timezone };
  }
  return null;
}

/**
 * The instant an event sorts at, so the two shapes can be ordered against each
 * other in one list.
 *
 * An all-day event has no instant of its own. It is anchored to the start of
 * its date **in the household's zone**, which is the only reading that puts a
 * birthday on the same day as the morning appointments it shares a header with.
 * This is a sorting key, not a time to display — nothing renders it.
 */
export function sortKey(event: Event, tz: string): number {
  if (event.startAt !== undefined) return event.startAt;
  if (event.startDate === undefined) return Number.POSITIVE_INFINITY;
  return startOfDateIn(event.startDate, tz);
}

/**
 * `YYYY-MM-DD` to the instant that day begins in `tz`.
 *
 * Probed rather than assumed: the offset is whatever the zone was at that
 * moment, which changes twice a year, and on a spring-forward date midnight
 * itself can be the hour that does not exist.
 *
 * Exported for `month.ts`, which bounds the grid's expansion window by the
 * first and last cell. That is the same question this already answers for the
 * day headers, and a second implementation of it would be the one that forgets
 * the probe.
 */
export function startOfDateIn(date: string, tz: string): number {
  return instantOfWall(Date.parse(`${date}T00:00:00Z`), tz);
}

/**
 * A **wall clock** back to the instant it names in `tz`.
 *
 * "Wall clock" here means the reading on the wall — 08:30 on 25 October —
 * carried as a UTC instant with those fields, which is the only shape a
 * `Date` can hold one in. `offsetAt` is the inverse direction: it turns an
 * instant into that same pseudo-UTC reading.
 *
 * This pair is what makes recurrence survive DST. An occurrence is expanded as
 * a wall clock and converted back **at its own moment**, so the two sides of a
 * boundary get the two different offsets that keep them both at 08:30. Convert
 * once at the series' start instead and every occurrence after the boundary is
 * an hour out.
 */
export function instantOfWall(wall: number, tz: string): number {
  /**
   * Two probes, and the second is the load-bearing one.
   *
   * A wall clock does not say which side of a transition it is on, so the only
   * way in is to guess. Reading the wall value *as if it were an instant* gives
   * an offset that is right except when a transition falls between the guess
   * and the truth; re-probing at the corrected instant is what lands on the
   * right side. On the ambiguous hour in October this resolves to the second
   * pass, which is `rrule`'s own convention and the one the tests assert.
   */
  const guess = wall - offsetAt(wall, tz);
  return wall - offsetAt(guess, tz);
}

/**
 * The offset of `tz` at an instant, in ms — equivalently, that instant's wall
 * clock as a pseudo-UTC instant, minus the instant itself.
 *
 * **One probe, and that is the whole of it.** This used to do the two-step
 * above and call the result "the offset at an instant", which it is not: the
 * two-step answers a different question — *which instant does this wall clock
 * name* — and returns the offset at a moment up to two hours earlier. Used in
 * this direction it was wrong for any event starting within an offset's width
 * after a transition, and shifted that event's entire series by an hour while
 * the detail screen went on showing the stored time. The two directions are
 * separate functions now because they are separate questions.
 */
export function offsetAt(at: number, tz: string): number {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    timeZone: tz,
  }).formatToParts(new Date(at));
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)!.value;
  // `h24` renders midnight as the previous date's hour 24; `h23` never does.
  // Mapping it to 00 on the same date is correct for both.
  const hour = get("hour") === "24" ? "00" : get("hour");
  return (
    Date.parse(
      `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}:${get("second")}Z`,
    ) - at
  );
}

/** One day's worth, under the header that names it. */
export type EventDay = {
  /** `YYYY-MM-DD` in the household's zone. The group's identity. */
  date: string;
  /** The instant that day starts, for the header's formatting. */
  at: number;
  events: Occurrence[];
};

/**
 * The only place the calendar's ordering lives.
 *
 * Events are grouped by the calendar day they fall on **in the household's
 * zone**, days ascending, and within a day all-day events come first — a
 * birthday is a property of the whole day, so it belongs above the 08:30 that
 * happens during it, not sorted into the middle by an anchor time nobody sees.
 *
 * Multi-day events appear on their start day only. Spanning them across every
 * day they cover is a real feature and deliberately not this slice's; doing it
 * halfway, so a week-long holiday shows up once and looks like a typo, would be
 * worse than not doing it.
 *
 * Takes **occurrences**, not events: by the time anything is grouped by day, a
 * series has already been expanded into the days it falls on. A non-recurring
 * event is an occurrence too — one of them — which is what lets every caller go
 * through `expandEvents` with no branch.
 */
export function groupByDay(events: Occurrence[], tz: string): EventDay[] {
  const days = new Map<string, EventDay>();

  for (const event of events) {
    const at = sortKey(event, tz);
    if (!Number.isFinite(at)) continue;

    const date = event.startDate ?? dateIn(at, tz);
    const day = days.get(date) ?? { date, at: startOfDateIn(date, tz), events: [] };
    day.events.push(event);
    days.set(date, day);
  }

  return [...days.values()]
    .sort((a, b) => a.at - b.at)
    .map((day) => ({
      ...day,
      events: day.events.sort(byAllDayThenStart(tz)),
    }));
}

function byAllDayThenStart(tz: string) {
  return (a: Event, b: Event) => {
    const allDay = Number(isAllDay(b)) - Number(isAllDay(a));
    if (allDay !== 0) return allDay;
    return sortKey(a, tz) - sortKey(b, tz) || a._creationTime - b._creationTime;
  };
}

/** The calendar date an instant falls on in `tz`, as `YYYY-MM-DD`. */
export const dateIn = (at: number, tz: string) =>
  new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit", timeZone: tz,
  }).format(new Date(at));
